import React, { useState, useEffect } from 'react';
import {
  Header,
  Table,
  Box,
  Pagination,
  TextFilter,
} from '@amzn/awsui-components-react/polaris';
import { useSdpdsReadOnlyOperation } from '../../../hooks/useSdpdsReadOnlyOperation';
import CollectionPreferences from "@amzn/awsui-components-react/polaris/collection-preferences";
import ElementErrorBoundary from '../../../components/ErrorBoundary/ElementErrorBoundary';
import { CLUSTER_PAGE_SIZE_OPTIONS } from '../constants';
import { getClusterColumns } from './columns';

export const ClusterInStageDetail = ({ stageId, stackName, releaseId }) => {
  const [isRequestActive, setIsRequestActive] = useState(true);
  const [clusters, setClusters] = useState([]);
  const [error, setError] = useState(null);
  const [nextToken, setNextToken] = useState(null);
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [filteringText, setFilteringText] = useState('');

  const { data, error: hookError } = useSdpdsReadOnlyOperation({
    operation_name: "ListClustersInReleaseStage",
    payload: {
      stackName,
      releaseId,
      stageId: Number(stageId),
      maxResults: 5000,
      nextToken: nextToken
    }
  });

  const updateResponse = (response) => {
    if (response) {
      const newClusters = response.clusters || [];
      setClusters(prevClusters => [...prevClusters, ...newClusters]);

      if (response.nextToken) {
        setNextToken(response.nextToken);
      } else {
        setIsRequestActive(false);
      }
    }
  };

  useEffect(() => {
    if (hookError) {
      setError(hookError.message || 'Failed to fetch cluster details');
      setIsRequestActive(false);
    } else if (data?.sdpdsReadOnlyOperation) {
      updateResponse(data.sdpdsReadOnlyOperation);
    }
  }, [data, hookError]);

  useEffect(() => {
    setClusters([]);
    setNextToken(null);
    setIsRequestActive(true);
    setError(null);
    setCurrentPageIndex(1);
  }, [stageId, stackName, releaseId]);

  const getFilteredItems = () => {
    if (!filteringText) return clusters;
    return clusters.filter(item =>
      item.cluster.toLowerCase().includes(filteringText.toLowerCase()) ||
      item.status.toLowerCase().includes(filteringText.toLowerCase())
    );
  };

  const getPagedItems = () => {
    const filteredItems = getFilteredItems();
    const startIndex = (currentPageIndex - 1) * pageSize;
    return filteredItems.slice(startIndex, startIndex + pageSize);
  };

  const handlePageChange = ({ detail }) => {
    setCurrentPageIndex(detail.currentPageIndex);
  };

  const handleFilterChange = ({ detail }) => {
    setFilteringText(detail.filteringText);
    setCurrentPageIndex(1);
  };

  const filteredItems = getFilteredItems();
  const pagedItems = getPagedItems();

  return (
    <ElementErrorBoundary>
      <Table
        columnDefinitions={getClusterColumns()}
        items={pagedItems}
        loading={isRequestActive}
        loadingText="Loading clusters"
        variant="embedded"
        sortingDisabled={false}
        empty={
          <Box textAlign="center" color="inherit">
            <b>No clusters to display</b>
          </Box>
        }
        filter={
          <TextFilter
            filteringPlaceholder="Filter data across all fields"
            filteringText={filteringText}
            onChange={handleFilterChange}
            countText={`${filteredItems.length} matches`}
          />
        }
        pagination={
          <Pagination
            currentPageIndex={currentPageIndex}
            onChange={handlePageChange}
            pagesCount={Math.ceil(filteredItems.length / pageSize)}
            pageSize={pageSize}
          />
        }
        preferences={
          <CollectionPreferences
            title="Preferences"
            confirmLabel="Confirm"
            cancelLabel="Cancel"
            preferences={{ pageSize }}
            pageSizePreference={{
              title: "Page size",
              options: CLUSTER_PAGE_SIZE_OPTIONS
            }}
            onConfirm={({ detail }) => {
              setPageSize(detail.pageSize);
              setCurrentPageIndex(1);
            }}
          />
        }
        header={
          <Header counter={`(${filteredItems.length})`}>
            Clusters in Stage {stageId}
          </Header>
        }
        wrapLines
        stickyHeader={false}
      />
      {error && <Box color="error">{error}</Box>}
    </ElementErrorBoundary>
  );
};
